
/// <amd-module name="Release/Landing/LeftPanel/ActivePipelines/ActivePipelinesStore" />

import { ReleaseDefinition, DeploymentAttempt, Deployment, DeploymentOperationStatus } from "Release/Client/Contracts/Release";
import { StoreBase } from "DistributedTask/Flux/Store";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { ActivePipelinesActions, IInitializeDefinitionReferencesPayload, IDefinitionAndDeploymentPayload, IUpdateReleaseEnvironmentStatusPayload, IReleaseEnvironmentStartPayload } from "./ActivePipelinesActions";
import { IActiveDefinitionReference, ActiveDefinitionReferenceType } from "Release/Landing/DataProviderContracts";
import { PipelineSearchStore } from "Release/Landing/LeftPanel/PipelineSearch/PipelineSearchStore";
import { getDefinitionReferenceFromDefinition } from "LandingUtils";
import { PipelinesActions, IRenamePipelinePayload, IRenameFolderPayload, IRemovePipelinePayload, IMovePipelinePayload } from "LeftPanel/PipelinesActions";
import { NamedStoreEvents } from "Release/Landing/Constants";

export interface IActivePipelinesState {
    activeDefinitions: IActiveDefinitionReference[] | null;
    recentDefinitions: IActiveDefinitionReference[] | null;
    searchedDefinitions: ReleaseDefinition[] | null;
    searchText: string;
    searchResultsLoading: boolean;
}

export class ActivePipelinesStore extends StoreBase {
    public static getKey(): string {
        return "release-landing-active-pipelines-store";
    }

    public initialize(instanceId?: string): void {
        super.initialize(instanceId);

        this._state = { activeDefinitions: null, recentDefinitions: null, searchedDefinitions: null, searchText: "", searchResultsLoading: false };

        this._actions = ActionsHubManager.GetActionsHub<ActivePipelinesActions>(ActivePipelinesActions);
        this._pipelinesActions = ActionsHubManager.GetActionsHub<PipelinesActions>(PipelinesActions);
        this._actions.initializeDefs.addListener(this._initializeDefs);
        this._actions.selectAdditionalPipeline.addListener(this._selectAdditionalPipeline);
        this._actions.onPipelineFavorited.addListener(this._onPipelineFavorited);
        this._actions.onRecentFavorited.addListener(this._onRecentFavorited);
        this._actions.updateReleaseEnvironmentStatus.addListener(this._onStatusUpdate);
        this._actions.handleDeployJobStarted.addListener(this._onDeploymentStart);

        this._pipelinesActions.removePipeline.addListener(this._onRemovePipeline);
        this._pipelinesActions.onRenamePipeline.addListener(this._onRenamePipeline);
        this._pipelinesActions.onMovePipeline.addListener(this._onMovePipeline);
        this._pipelinesActions.onRenameFolder.addListener(this._onRenameFolder);
        this._pipelinesActions.removeFolder.addListener(this._onRemoveFolder);

        this._pipelineSearchStore = StoreManager.GetStore<PipelineSearchStore>(PipelineSearchStore, instanceId);
        this._pipelineSearchStore.addChangedListener(this._onSearchPipelineStoreChange);
    }

    public disposeInternal(): void {
        this._actions.initializeDefs.removeListener(this._initializeDefs);
        this._actions.selectAdditionalPipeline.removeListener(this._selectAdditionalPipeline);
        this._actions.onPipelineFavorited.removeListener(this._onPipelineFavorited);
        this._actions.onRecentFavorited.removeListener(this._onRecentFavorited);
        this._actions.updateReleaseEnvironmentStatus.addListener(this._onStatusUpdate);
        this._actions.handleDeployJobStarted.addListener(this._onDeploymentStart);

        this._pipelinesActions.removePipeline.removeListener(this._onRemovePipeline);
        this._pipelinesActions.onRenamePipeline.removeListener(this._onRenamePipeline);
        this._pipelinesActions.onMovePipeline.removeListener(this._onMovePipeline);
        this._pipelinesActions.onRenameFolder.removeListener(this._onRenameFolder);
        this._pipelinesActions.removeFolder.removeListener(this._onRemoveFolder);

        this._pipelineSearchStore.removeChangedListener(this._onSearchPipelineStoreChange);
    }

    public getState(): IActivePipelinesState {
        return this._state;
    }

    private _initializeDefs = (payload: IInitializeDefinitionReferencesPayload): void => {
        this._state.activeDefinitions = payload.activeDefinitions;
        this._state.recentDefinitions = payload.recentDefinitions;
        this.emitChanged(); // Todo: Find a way to initialize through which we can avoid doing emitChanged at this stage
    }

    private _onSearchPipelineStoreChange = (): void => {
        const pipelineSearchState = this._pipelineSearchStore.getState();
        this._state.searchText = pipelineSearchState.searchText;
        this._state.searchedDefinitions = pipelineSearchState.searchedDefinitions;
        this._state.searchResultsLoading = pipelineSearchState.searchResultsLoading;
        this.emitChanged();
    }

    private _selectAdditionalPipeline = (payload: IDefinitionAndDeploymentPayload): void => {
        const definition = this._getActiveDefinitionReference(payload);

        if (definition) {
            if (!this._state.recentDefinitions) {
                this._state.recentDefinitions = [];
            }

            this._state.recentDefinitions.unshift(definition);
            this.emitChanged();
        }
    }

    private _onPipelineFavorited = (payload: IDefinitionAndDeploymentPayload): void => {
        const definition = this._getActiveDefinitionReference(payload);

        if (definition) {
            this._addFavoriteToActiveDefinitions(definition);
            this.emitChanged();
        }
    }

    private _onRecentFavorited = (id: number): void => {
        if (this._state.recentDefinitions) {
            const index = this._state.recentDefinitions.findIndex((def) => def.id === id);
            if (index >= 0) {
                this._addFavoriteToActiveDefinitions(this._state.recentDefinitions[index]);
                this._state.recentDefinitions.splice(index, 1);
                this.emitChanged();
            }
        }
    }

    private _onRemovePipeline = (payload: IRemovePipelinePayload): void => {
        const activeDefinitions = this._state.activeDefinitions;
        const recentDefinitions = this._state.recentDefinitions;
        let update = false;
        if (activeDefinitions) {
            const index = activeDefinitions.findIndex((def) => def.id === payload.pipelineId);
            if (index >= 0) {
                activeDefinitions.splice(index, 1);
                update = true;
            }
        }

        if (recentDefinitions) {
            const index = recentDefinitions.findIndex((def) => def.id === payload.pipelineId);
            if (index >= 0) {
                recentDefinitions.splice(index, 1);
                update = true;
            }
        }

        if (update) {
            this.emitChanged();
        }
    }

    private _onRemoveFolder = (folderPath: string): void => {
        const activeDefinitions = this._state.activeDefinitions;
        const recentDefinitions = this._state.recentDefinitions;
        let update = false;
        if (activeDefinitions) {
            let indicesToRemove: number[] = [];
            activeDefinitions.forEach((def, index) => {
                if (def.path.indexOf(folderPath) === 0) {
                    indicesToRemove.push(index);
                }
            });

            if (indicesToRemove.length) {
                indicesToRemove.forEach(index => activeDefinitions.splice(index, 1));
                update = true;
            }
        }

        if (recentDefinitions) {
            let indicesToRemove: number[] = [];
            recentDefinitions.forEach((def, index) => {
                if (def.path.indexOf(folderPath) === 0) {
                    indicesToRemove.push(index);
                }
            });

            if (indicesToRemove.length) {
                indicesToRemove.forEach(index => recentDefinitions.splice(index, 1));
                update = true;
            }
        }

        if (update) {
            this.emitChanged();
        }
    }

    private _onRenamePipeline = (payload: IRenamePipelinePayload): void => {
        const activeDefinitions = this._state.activeDefinitions;
        const recentDefinitions = this._state.recentDefinitions;
        const update = updatePipeline(activeDefinitions, recentDefinitions, payload.pipelineId, (pipe) => pipe.name = payload.pipelineName);
        if (update) {
            this.emitChanged();
            this.emit(NamedStoreEvents.Pileline_Renamed_Moved_Event, {});
        }
    }

    private _onMovePipeline = (payload: IMovePipelinePayload): void => {
        const activeDefinitions = this._state.activeDefinitions;
        const recentDefinitions = this._state.recentDefinitions;
        const update = updatePipeline(activeDefinitions, recentDefinitions, payload.pipelineId, (pipe) => pipe.path = payload.newFolderPath);

        if (update) {
            this.emitChanged();
            this.emit(NamedStoreEvents.Pileline_Renamed_Moved_Event, {});
        }
    }

    private _onStatusUpdate = (payload: IUpdateReleaseEnvironmentStatusPayload): void => {
        const relevantPipelines = [...(this._state.activeDefinitions || []), ...(this._state.recentDefinitions || [])];
        const matchingPipeline = relevantPipelines.find(pipe => pipe.id === payload.definitionId);
        if (matchingPipeline && matchingPipeline.lastDeployment) {
            const deployment = matchingPipeline.lastDeployment;
            if (deployment.releaseEnvironment && deployment.releaseEnvironment.id === payload.releaseEnvironmentId) {
                deployment.deploymentStatus = payload.deploymentStatus;
                deployment.operationStatus = payload.operationStatus;
                this.emitChanged();
            }
        }
        else if (matchingPipeline
            && matchingPipeline.pendingApproval
            && matchingPipeline.pendingApproval.releaseEnvironment.id === payload.releaseEnvironmentId
            && payload.operationStatus !== DeploymentOperationStatus.Pending) {
            const approval = matchingPipeline.pendingApproval;
            matchingPipeline.lastDeployment = {
                attempt: approval.attempt,
                deploymentStatus: payload.deploymentStatus,
                operationStatus: payload.operationStatus,
                releaseEnvironment: approval.releaseEnvironment
            } as Deployment;

            matchingPipeline.pendingApproval = undefined;
            this.emitChanged();
        }
    }

    private _onDeploymentStart = (payload: IReleaseEnvironmentStartPayload): void => {
        const relevantPipelines = [...(this._state.activeDefinitions || []), ...(this._state.recentDefinitions || [])];
        const matchingPipeline = relevantPipelines.find(pipe => pipe.id === payload.definitionId);
        const environment = payload.environment;
        if (matchingPipeline && !matchingPipeline.pendingApproval) {
            const maxAttempt = Math.max(...environment.deploySteps.map(d => d.attempt));
            const deployment: DeploymentAttempt | undefined = environment.deploySteps.find(d => d.attempt === maxAttempt);
            if (deployment) {
                matchingPipeline.lastDeployment = {
                    id: deployment.deploymentId,
                    attempt: deployment.attempt,
                    deploymentStatus: deployment.status,
                    operationStatus: deployment.operationStatus,
                    releaseEnvironment: { id: environment.id, name: environment.name }
                } as Deployment;

                this.emitChanged();
            }
        }
    }


    private _onRenameFolder = (payload: IRenameFolderPayload): void => {
        const activeDefinitions = this._state.activeDefinitions;
        const recentDefinitions = this._state.recentDefinitions;
        let update = false;
        if (activeDefinitions) {
            activeDefinitions.filter(def => def.path.indexOf(payload.oldFolderPath as string) === 0).forEach((filteredDef) => {
                filteredDef.path = filteredDef.path.replace(payload.oldFolderPath as string, payload.folderPath as string);
                update = true;
            });
        }

        if (recentDefinitions) {
            recentDefinitions.filter(def => def.path.indexOf(payload.oldFolderPath as string) === 0).forEach((filteredDef) => {
                filteredDef.path = filteredDef.path.replace(payload.oldFolderPath as string, payload.folderPath as string);
                update = true;
            });
        }

        if (update) {
            this.emitChanged();
        }
    }

    private _addFavoriteToActiveDefinitions(definition: IActiveDefinitionReference): void {
        if (!this._state.activeDefinitions) {
            this._state.activeDefinitions = [];
        }

        // TODO - soro : Refine this logic. We can't just smack this at the beginning of the list. Sort and determine the location of this item
        this._state.activeDefinitions.unshift(definition);
    }

    private _getActiveDefinitionReference(payload: IDefinitionAndDeploymentPayload): IActiveDefinitionReference | undefined {
        const rd = payload.definition;
        if (rd) {
            return getDefinitionReferenceFromDefinition(rd, ActiveDefinitionReferenceType.SearchedResult, payload.deployment);
        }

        const definitionReference = payload.definitionReference;
        if (definitionReference) {
            definitionReference.lastDeployment = payload.deployment;
            return definitionReference;
        }
    }

    private _state: IActivePipelinesState;
    private _actions: ActivePipelinesActions;
    private _pipelinesActions: PipelinesActions;
    private _pipelineSearchStore: PipelineSearchStore;
}

function updatePipeline(activeDefinitions: IActiveDefinitionReference[] | null, recentDefinitions: IActiveDefinitionReference[] | null, pipelineId: number, updateFunc: (def: IActiveDefinitionReference) => void): boolean {
    let update = false;
    if (activeDefinitions) {
        const match = activeDefinitions.find(def => def.id === pipelineId);
        if (match) {
            updateFunc(match);
            update = true;
        }
    }

    if (recentDefinitions) {
        const match = recentDefinitions.find(def => def.id === pipelineId);
        if (match) {
            updateFunc(match);
            update = true;
        }
    }

    return update;
}